import { Dot, type DotTone } from "@/components/ui/dot";
import { getConfidenceLevelLabel } from "@/lib/display-labels";
import type {
  ConfidenceBasisKey,
  ConfidenceLevel,
  TrackRecordSourceKey,
} from "@opus-dt/shared-types";

const TONE: Record<ConfidenceLevel, DotTone> = {
  HIGH: "success",
  MEDIUM: "info",
  LOW: "warning",
  INSUFFICIENT: "danger",
};

// Spec 079 (FR-028) — A BASE EM LINGUAGEM DE AÇÃO, não em jargão de esquema.
//
// Duas bases, duas ações OPOSTAS: histórico ruim pede calibrar o modelo; procedência fraca pede
// melhorar o cadastro do dado. Exibir só "Baixa" deixa o gestor sem saber para qual dos dois lados
// andar — e as duas frases custam uma linha de texto.
//
// A régua da cascata refina a frase porque refina a AÇÃO: um histórico ruim só deste par
// linha×produto é um problema de receita; da linha inteira, de modelo; da fábrica, de método.
const BASIS_LABEL: Record<ConfidenceBasisKey, string> = {
  provenance: "pela origem dos dados",
  track_record: "pelo histórico de acerto",
};

const SOURCE_LABEL: Record<TrackRecordSourceKey, string> = {
  line_product: "pelo histórico desta linha com este produto",
  line: "pelo histórico desta linha",
  organization: "pelo histórico da fábrica",
};

/**
 * O trio que o chip desenha, junto (Spec 079/FR-018).
 *
 * Exportado porque quem passa o nível adiante passa a base junto — separá-los em props soltas fez
 * o `ApplyScenarioDialog` precisar de seis parâmetros para desenhar dois chips, e é exatamente
 * assim que a base começa a chegar num call site e não no outro.
 */
export interface ConfidenceDisplay {
  level: ConfidenceLevel | null;
  /** `null`/ausente = execução anterior à Spec 079, que não declara base. O chip omite a frase. */
  basis?: ConfidenceBasisKey | null;
  trackRecordSource?: TrackRecordSourceKey | null;
}

export interface ConfidenceChipProps extends ConfidenceDisplay {
  /**
   * Grade apertada (o comparativo lado a lado): a frase da base vira `title`, e o chip não cresce.
   *
   * ⚠️ NÃO é "esconder a informação": o dot e o rótulo do nível continuam, e a base fica alcançável
   * no hover e pelo leitor de tela. É a única superfície do produto onde a frase não cabe.
   */
  compact?: boolean;
}

/** A frase da base, ou `null` quando a execução não a declara. */
export function confidenceBasisLabel(
  basis: ConfidenceBasisKey | null | undefined,
  source: TrackRecordSourceKey | null | undefined
): string | null {
  if (!basis) return null;
  if (basis === "track_record" && source) return SOURCE_LABEL[source];
  return BASIS_LABEL[basis];
}

// Confiança como chip + dot + label, nunca só por cor.
// Degradação elegante: nível ausente vira "—" neutro.
export function ConfidenceChip({ level, basis, trackRecordSource, compact }: ConfidenceChipProps) {
  const tone: DotTone = level ? TONE[level] : "neutral";
  const reason = confidenceBasisLabel(basis, trackRecordSource);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-sm font-medium"
        title={compact && reason ? reason : undefined}
      >
        <Dot tone={tone} />
        {level ? getConfidenceLevelLabel(level) : "—"}
        {/* ⚠️ No modo compacto a frase existia SÓ no `title`, e `title` não existe em toque nem é
            anunciado por leitor de tela num `<span>` que já tem texto próprio. A base é o que
            separa duas ações OPOSTAS (calibrar o modelo × arrumar o cadastro do dado) — escondê-la
            atrás de hover é escondê-la de quem opera no tablet do chão de fábrica. O `sr-only`
            devolve a frase a quem não tem ponteiro, sem alargar o crachá na grade apertada. */}
        {compact && reason ? <span className="sr-only"> — {reason}</span> : null}
      </span>
      {reason && !compact ? <span className="text-xs text-muted-foreground">{reason}</span> : null}
    </span>
  );
}
